/**
 * When a new build may take over the page.
 *
 * 🔴 THE PROBLEM THIS SOLVES IS "EVERY DEPLOY NEEDS A HARD REFRESH". A new build
 * has to reach a tab that a school laptop keeps open all day, and the tab has to
 * move onto it without a child noticing. The whole question is WHEN.
 *
 * ⚠️ NEVER IN THE MIDDLE OF A CHILD'S ROUND. A reload there throws away the
 * round they were about to finish. So the kid flow `hold`s updates for the
 * length of a round, and the update waits for the first moment nobody would
 * lose anything:
 *   · the tab is hidden — nobody is looking;
 *   · the route changes — the screen is being replaced anyway;
 *   · a screen declares a safe point — the kid flow between rounds;
 *   · nobody has touched the page for two minutes — an idle dashboard.
 *
 * What "applying" means is the caller's: usually telling the waiting worker to
 * take over, then reloading onto it (`serviceWorker.ts`).
 */

type Apply = () => void

const IDLE_MS = 2 * 60 * 1000

const holds = new Set<string>()
let pending = false
let lastInput = Date.now()
let reloadPage: Apply = () => {
  window.location.reload()
}
let apply: Apply = () => {
  reloadPage()
}

/** Blocks the update until the returned function is called. */
export function holdUpdates(reason: string): () => void {
  holds.add(reason)
  return () => {
    holds.delete(reason)
  }
}

/**
 * A new build is ready for this page. `applyUpdate` is what to do at the safe
 * moment; without one, the page simply reloads.
 */
export function markUpdateReady(applyUpdate?: Apply): void {
  pending = true
  apply =
    applyUpdate ??
    (() => {
      reloadPage()
    })
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') settle()
}

/** "Nothing on this screen would be lost by an update now." */
export function updateSafePoint(): void {
  settle()
}

/** Whether an update is waiting for its moment — for a status chip, and for tests. */
export function updatePending(): boolean {
  return pending
}

function settle(): void {
  if (!pending || holds.size > 0) return
  pending = false
  apply()
}

/** Wires the moments above to the browser. Called once, by `startServiceWorker`. */
export function watchForSafeMoments(): void {
  const touch = (): void => {
    lastInput = Date.now()
  }
  window.addEventListener('pointerdown', touch, { passive: true })
  window.addEventListener('keydown', touch, { passive: true })
  window.addEventListener('hashchange', settle)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') settle()
  })
  window.setInterval(() => {
    if (Date.now() - lastInput >= IDLE_MS) settle()
  }, 30 * 1000)
}

/** Test seam: swap the page reload, and reset between cases. */
export function __setReloadForTests(fn: Apply): void {
  reloadPage = fn
  apply = () => {
    reloadPage()
  }
  holds.clear()
  pending = false
}
